export default {
  name: 'blockBuilder',
  component(settings, colorSchema = '', type = '') {
    // Utility: Re-parent block-builder or sticky-block-builder to a new parent on load
    function reparentBlockBuilder(settings, el) {
      if (!settings || !settings.reparent_to_selector) return;

      const blockEl = el;
      // Find the new parent element by selector
      const newParent = document.querySelector(settings.reparent_to_selector);

      if (!newParent) return;

      // Optionally, insert at a specific child index
      if (typeof settings.reparent_child_index === 'number') {
        const children = Array.from(newParent.children);
        if (settings.reparent_child_index >= 0 && settings.reparent_child_index < children.length) {
          newParent.insertBefore(blockEl, children[settings.reparent_child_index]);
        } else {
          newParent.appendChild(blockEl);
        }
      } else {
        newParent.appendChild(blockEl);
      }
    }

    return {
      componentId: Math.random().toString(36).substr(2, 9),
      isVisible: !settings.interaction_controlled || settings.default_visible,
      targetEventIds:
        settings && settings.target_event_ids ? settings.target_event_ids.split(',').map((item) => item.trim()) : [],
      targetEventId: settings.target_event_id || '',
      hasSticky: settings.always_show_bg || false,
      classes: '',
      isLatchSource: settings.is_latch_source || false,
      isLatchTarget: settings.is_latch_target || false,
      latchedContentVisible: false,
      hoverLeaveTimeout: null,
      eventBehaviorMap: new Map(), // Initialize as reactive property
      backdropElement: null, // Explicitly initialize backdrop as null
      // Collapsible functionality
      isCollapsed: settings.collapsed_by_default || false,
      // Force reactivity for render type changes
      renderTypeKey: 0,
      // Animation state management
      isAnimating: false,
      animationDirection: 'in', // 'in' or 'out'
      actuallyVisible: !settings.interaction_controlled || settings.default_visible,
      animationStart: false,

      // Action response configuration
      actionResponseEnabled: settings.enable_action_responses || false,
      actionResponseType: settings.action_response_type || 'drawer',
      actionInitialState: settings.action_initial_state || 'hidden',
      actionCurrentState: settings.action_initial_state === 'visible' ? 'visible' : 'hidden',

      // Computed property to determine final visibility
      get shouldBeVisible() {
        // Access renderTypeKey to ensure reactivity on resize
        this.renderTypeKey;

        // If interaction control is not enabled, let CSS handle visibility entirely
        if (!settings.interaction_controlled) {
          return true; // Don't interfere with CSS classes
        }

        // For animation purposes, we use actuallyVisible instead of isVisible
        // This allows for proper animation timing
        if (!this.actuallyVisible) {
          return false;
        }

        // Then check render type restrictions for interaction-controlled elements
        return this.checkRenderTypeVisibility();
      },

      init() {
        this.$nextTick(() => {
          reparentBlockBuilder(settings, this.$el);

          if (settings.animation_trigger == 'onload') {
            setTimeout(() => {
              this.animationStart = true;
            }, settings.animation_delay || 0);
          }
        });

        // Watch for screen size changes that might affect render type visibility
        let resizeTimeout;
        window.addEventListener('resize', () => {
          // Debounce resize events
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            // Force reactivity update
            this.renderTypeKey++;

            // Reapply responsive styles for popup/drawer if action responses are enabled
            if (this.actionResponseEnabled && this.$el) {
              // Store current visibility state before reapplying styles
              const currentState = this.actionCurrentState;
              this.applyActionResponseStyles();

              // Restore the correct transform state after style reapplication
              if (currentState === 'visible') {
                requestAnimationFrame(() => {
                  switch (this.actionResponseType) {
                    case 'drawer':
                      this.$el.style.transform = this.getDrawerVisibleTransform();
                      break;
                    case 'popup':
                      this.$el.style.transform = this.getPopupVisibleTransform();
                      const animationStyle = settings.popup_animation_style || 'fade';
                      if (
                        animationStyle === 'fade' ||
                        animationStyle === 'zoom' ||
                        animationStyle === 'slide-up' ||
                        animationStyle === 'slide-down'
                      ) {
                        this.$el.style.opacity = '1';
                      }
                      break;
                    case 'toggle':
                      this.$el.style.opacity = '1';
                      break;
                  }
                });
              }
            }
          }, 100);
        });

        // Handle max height calculation for screen_minus_reparent type
        if (settings.max_height_type === 'screen_minus_reparent' && settings.reparent_to_selector) {
          this.setupReparentHeightCalculation();
        }

        // Setup action response handlers if enabled
        if (this.actionResponseEnabled) {
          this.setupActionResponse();
        }

        // Dispatch initial trigger color state on page load
        this.$nextTick(() => {
          this.dispatchInitialTriggerColorState(0); // Start with retry count 0
        });
      },

      // Setup action response functionality
      setupActionResponse() {
        if (!this.actionResponseEnabled || !this.$el) return;

        // Apply initial styling based on action response type
        this.applyActionResponseStyles();

        // Set initial visibility state immediately
        if (this.actionInitialState === 'hidden') {
          this.$el.style.display = 'none';
          this.$el.style.pointerEvents = 'none';
          // For toggle type, also set opacity
          if (this.actionResponseType === 'toggle') {
            this.$el.style.opacity = '0';
          }

          this.$el.classList.remove('!hidden');
        } else if (this.actionInitialState === 'visible') {
          // Ensure element is visible for initial visible state
          this.$el.style.display = 'block';
          this.$el.style.pointerEvents = 'auto';
          // Set appropriate styles based on response type
          switch (this.actionResponseType) {
            case 'toggle':
              this.$el.style.opacity = '1';
              break;
            case 'drawer':
              this.$el.style.transform = this.getDrawerVisibleTransform();
              break;
            case 'popup':
              this.$el.style.transform = this.getPopupVisibleTransform();
              // Only set opacity for animations that use it
              const animationStyle = settings.popup_animation_style || 'fade';
              if (
                animationStyle === 'fade' ||
                animationStyle === 'zoom' ||
                animationStyle === 'slide-up' ||
                animationStyle === 'slide-down'
              ) {
                this.$el.style.opacity = '1';
              }
              break;
          }
        }

        this.setActionState(this.actionInitialState);

        // Listen for custom event based on event_id
        const eventId = settings.event_id;
        if (eventId) {
          window.addEventListener(eventId, (event) => {
            // Toggle behavior by default for interaction control
            this.handleActionEvent('toggle');
          });
        }

        // Setup escape key handler
        if (settings.action_close_on_escape) {
          document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.actionCurrentState === 'visible') {
              // Trigger custom actions first if specified
              const closeCustomActions = settings.close_custom_actions;
              if (closeCustomActions && closeCustomActions.trim() !== '') {
                this.triggerCloseCustomActions(closeCustomActions, 'escape_key');
              }
              // Then handle close action
              this.handleActionEvent('hide');
            }
          });
        }

        // Setup auto-close timer
        if (settings.action_auto_close_delay > 0) {
          this.$watch('actionCurrentState', (state) => {
            if (state === 'visible') {
              setTimeout(() => {
                if (this.actionCurrentState === 'visible') {
                  this.handleActionEvent('hide');
                }
              }, settings.action_auto_close_delay);
            }
          });
        }

        // Setup click outside detection
        if (settings.action_close_on_click_outside) {
          this.setupClickOutsideHandler();
        }
      },

      // Setup click outside detection
      setupClickOutsideHandler() {
        if (!this.$el) return;

        const componentInstance = this;
        // Use the inner content element instead of the padded wrapper
        const contentElement = this.$el.querySelector('.block-builder-container');
        const element = contentElement || this.$el; // Fallback to original element if content not found

        // Create the click outside handler
        this.clickOutsideHandler = function (event) {
          // Only handle clicks when the component is visible
          if (componentInstance.actionCurrentState !== 'visible') return;

          const target = event.target;

          // Special handling for form elements (including hidden ones)
          if (
            target.matches('input, textarea, select, button, label') ||
            target.closest('input, textarea, select, button, label')
          ) {
            // For checkboxes (including hidden ones), check if they're within the component
            // OR if they're associated with elements that are within the component
            if (target.type === 'checkbox') {
              // If checkbox is directly in component, don't close
              if (element.contains(target)) {
                return;
              }

              // If checkbox is outside but has an associated label inside the component, don't close
              if (target.id) {
                const associatedLabel = document.querySelector(`label[for="${target.id}"]`);
                if (associatedLabel && element.contains(associatedLabel)) {
                  return;
                }
              }

              // Check if any label inside the component contains this checkbox
              const labelsInComponent = element.querySelectorAll('label');
              for (const label of labelsInComponent) {
                if (label.htmlFor === target.id || label.contains(target)) {
                  return;
                }
              }
            }

            // For labels, check if they contain checkboxes or are associated with checkboxes
            if (target.tagName === 'LABEL') {
              // If the label itself is inside the component, don't close
              if (element.contains(target)) {
                return;
              }

              // Check for nested checkboxes
              const nestedCheckbox = target.querySelector('input[type="checkbox"]');
              if (nestedCheckbox && element.contains(nestedCheckbox)) {
                return;
              }

              // Check for associated checkboxes via htmlFor (even if checkbox is outside)
              if (target.htmlFor) {
                const associatedCheckbox = document.getElementById(target.htmlFor);
                if (associatedCheckbox && associatedCheckbox.type === 'checkbox') {
                  return;
                }
              }
            }

            // Check if clicking on an element inside a label that's associated with a checkbox
            const parentLabel = target.closest('label');
            if (parentLabel) {
              const nestedCheckbox = parentLabel.querySelector('input[type="checkbox"]');
              if (nestedCheckbox && element.contains(nestedCheckbox)) {
                return;
              }

              if (parentLabel.htmlFor) {
                const associatedCheckbox = document.getElementById(parentLabel.htmlFor);
                if (
                  associatedCheckbox &&
                  associatedCheckbox.type === 'checkbox' &&
                  element.contains(associatedCheckbox)
                ) {
                  return;
                }
              }
            }
          }

          // Check if the click is outside the component element
          if (!element.contains(event.target)) {
            // Also check if click is not on the backdrop (if it exists)
            if (componentInstance.backdropElement && componentInstance.backdropElement.contains(event.target)) {
              return; // Let backdrop handle the click
            }

            // Final safety check: if this appears to be a synthetic event from a form interaction,
            // and we detected form elements earlier, don't close
            if (event.isTrusted === false || (event.target && event.target.type === 'checkbox')) {
              return;
            }

            // Trigger custom actions first if specified
            const closeCustomActions = settings.close_custom_actions;
            if (closeCustomActions && closeCustomActions.trim() !== '') {
              componentInstance.triggerCloseCustomActions(closeCustomActions, 'outside_click');
            }

            // Then handle close action
            componentInstance.handleActionEvent('hide');
          }
        };

        // Add event listener to document
        document.addEventListener('click', this.clickOutsideHandler);

        // Store a reference for cleanup
        this.hasClickOutsideHandler = true;
      },

      // Cleanup click outside handler
      cleanupClickOutsideHandler() {
        if (this.clickOutsideHandler && this.hasClickOutsideHandler) {
          document.removeEventListener('click', this.clickOutsideHandler, true);
          this.clickOutsideHandler = null;
          this.hasClickOutsideHandler = false;
        }
      },

      // Handle custom event trigger (for Alpine.js event listeners)
      handleEventTrigger(event) {
        if (this.actionResponseEnabled) {
          const { type } = event;

          let interactionType = 'toggle';
          if (type.includes('show')) {
            interactionType = 'show';
          } else if (type.includes('hide')) {
            interactionType = 'hide';
          }

          this.handleActionEvent(interactionType);
        }
      },

      // Apply styles based on action response type
      applyActionResponseStyles() {
        if (!this.$el) return;

        const element = this.$el;

        // Apply styles based on response type
        switch (this.actionResponseType) {
          case 'drawer':
            this.applyDrawerStyles(element);
            break;
          case 'popup':
            this.applyPopupStyles(element);
            break;
          case 'toggle':
            this.applyToggleStyles(element);
            break;
        }

        // Apply transition styles
        const duration = settings.action_transition_duration;
        const easing = settings.action_transition_easing || 'ease-out';
        element.style.transition = `all ${duration}ms ${easing}`;
      },

      // Apply drawer-specific styles
      applyDrawerStyles(element) {
        const direction = settings.drawer_direction || 'right';
        const isMobile = window.innerWidth <= 1024;
        const width =
          isMobile && settings.drawer_width_mobile ? settings.drawer_width_mobile : settings.drawer_width || '400px';

        element.style.position = 'fixed';
        element.style.zIndex = '1000';
        element.style.background = 'white';

        // Set initial transform based on direction (hidden state)
        // This will be overridden in setupActionResponse if initial state is visible
        switch (direction) {
          case 'left':
            element.style.top = '0';
            element.style.left = '0';
            element.style.height = '100dvh';
            if (width !== 'auto') {
              element.style.maxWidth = width;
              element.style.width = '100%';
            } else {
              element.style.width = width;
            }
            element.style.transform = 'translateX(-100%)';
            break;
          case 'right':
            element.style.top = '0';
            element.style.right = '0';
            element.style.height = '100dvh';
            if (width !== 'auto') {
              element.style.maxWidth = width;
              element.style.width = '100%';
            } else {
              element.style.width = width;
            }
            element.style.transform = 'translateX(100%)';
            break;
          case 'top':
            element.style.top = '0';
            element.style.left = '0';
            element.style.width = '100%';
            element.style.height = width;
            element.style.transform = 'translateY(-100%)';
            break;
          case 'bottom':
            element.style.bottom = '0';
            element.style.left = '0';
            element.style.width = '100%';
            element.style.height = width;
            element.style.transform = 'translateY(100%)';
            break;
        }
      },

      // Apply popup-specific styles
      applyPopupStyles(element) {
        const position = settings.popup_position || 'center';
        const isMobile = window.innerWidth <= 1024;
        const width =
          isMobile && settings.popup_width_mobile ? settings.popup_width_mobile : settings.popup_width || 'auto';
        const height = settings.popup_height || 'auto';
        const offset = settings.popup_offset || '0px';
        const animationStyle = settings.popup_animation_style || 'fade';

        element.style.position = 'fixed';
        element.style.zIndex = '1000';
        element.style.background = 'white';
        element.style.borderRadius = '8px';
        element.style.boxShadow = '0 10px 25px rgba(0,0,0,0.2)';
        if (width !== 'auto') {
          element.style.maxWidth = width;
          element.style.width = '100%';
        } else {
          element.style.width = width;
        }
        if (height !== 'auto') {
          element.style.maxHeight = height;
          element.style.height = '100dvh';
        } else {
          element.style.height = height;
        }

        // Set initial opacity for popup (hidden state)
        // This will be overridden in setupActionResponse if initial state is visible
        if (
          animationStyle === 'fade' ||
          animationStyle === 'zoom' ||
          animationStyle === 'slide-up' ||
          animationStyle === 'slide-down'
        ) {
          element.style.opacity = '0';
        }

        // Position based on setting and apply initial hidden transform
        switch (position) {
          case 'center':
            element.style.top = '50%';
            element.style.left = '50%';
            break;
          case 'top':
            element.style.top = offset;
            element.style.left = '50%';
            break;
          case 'bottom':
            element.style.bottom = offset;
            element.style.left = '50%';
            break;
          case 'left':
            element.style.top = '50%';
            element.style.left = offset;
            break;
          case 'right':
            element.style.top = '50%';
            element.style.right = offset;
            break;
          case 'top-left':
            element.style.top = offset;
            element.style.left = offset;
            break;
          case 'top-right':
            element.style.top = offset;
            element.style.right = offset;
            break;
          case 'bottom-left':
            element.style.bottom = offset;
            element.style.left = offset;
            break;
          case 'bottom-right':
            element.style.bottom = offset;
            element.style.right = offset;
            break;
        }
        // Set initial transform to hidden state
        element.style.transform = this.getPopupHiddenTransform();
      },

      // Apply toggle visibility styles
      applyToggleStyles(element) {
        // Basic toggle - just control visibility
        // Don't set initial opacity here - it will be set in setActionState based on initial state
        // Only set pointerEvents to none if the element is currently hidden
        if (this.actionCurrentState === 'hidden') {
          element.style.pointerEvents = 'none';
        } else {
          element.style.pointerEvents = 'auto';
        }
      },

      // Create backdrop element
      createBackdrop() {
        if (this.backdropElement) {
          // Check if the existing backdrop is still in the DOM
          if (!this.backdropElement.parentNode) {
            this.backdropElement = null;
          } else {
            return;
          }
        }

        this.backdropElement = document.createElement('div');
        this.backdropElement.setAttribute('data-component-id', this.componentId);
        const backdropZIndex = settings.action_backdrop_z_index || 20;

        this.backdropElement.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100dvh;
          background: rgba(0,0,0,0.5);
          z-index: ${backdropZIndex};
          opacity: 0;
          pointer-events: none;
          transition: opacity ${settings.action_transition_duration || 300}ms ease;
        `;

        // Handle backdrop click actions
        const closeOnBackdropClick = settings.action_close_on_backdrop_click || false;
        const closeCustomActions = settings.close_custom_actions;

        if (closeOnBackdropClick || (closeCustomActions && closeCustomActions.trim() !== '')) {
          const componentInstance = this;
          this.backdropElement.addEventListener('click', function (event) {
            event.stopPropagation();
            // Trigger custom actions first if specified
            if (closeCustomActions && closeCustomActions.trim() !== '') {
              componentInstance.triggerCloseCustomActions(closeCustomActions, 'backdrop_click');
            }
            // Then handle close action if enabled
            if (closeOnBackdropClick) {
              componentInstance.handleActionEvent('hide');
            }
          });
        }

        document.body.appendChild(this.backdropElement);
      },

      // Trigger custom actions from close events (backdrop, outside click, escape key)
      triggerCloseCustomActions(customActions, source = 'close') {
        if (!customActions || customActions.trim() === '') return;

        // Split by commas and process each event ID
        const eventIds = customActions.split(',');
        eventIds
          .map((eventId) => eventId.trim())
          .filter(Boolean)
          .forEach((eventId) => {
            // Dispatch custom event for each action
            window.dispatchEvent(
              new CustomEvent(eventId, {
                detail: {
                  source: source,
                  componentId: this.componentId,
                  timestamp: Date.now()
                }
              })
            );
          });
      },

      // Handle action events
      handleActionEvent(actionType) {
        // Prevent toggling during animations
        if (this.isAnimating) {
          return;
        }

        switch (actionType) {
          case 'show':
            this.setActionState('visible');
            break;
          case 'hide':
            this.setActionState('hidden');
            break;
          case 'toggle':
            this.setActionState(this.actionCurrentState === 'visible' ? 'hidden' : 'visible');
            break;
        }
      },

      // Set action state with animations
      setActionState(state) {
        const shouldHaveBackdrop = settings.action_backdrop || false;

        if (!this.$el || this.actionCurrentState === state) return;

        // Set animation state to true at the start of any transition
        this.isAnimating = true;
        const previousState = this.actionCurrentState;
        this.actionCurrentState = state;
        const element = this.$el;

        // Notify header hover detection when state changes for reparented components
        if (settings.reparent_to_selector) {
          // Prioritize hidden events to ensure they process before visible events
          // This prevents losing header hover state when elements swap visibility
          const dispatchDelay = state === 'hidden' ? 0 : 15;

          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('block-builder-state-change', {
                detail: {
                  state: state,
                  reparentSelector: settings.reparent_to_selector,
                  eventId: settings.event_id,
                  componentId: this.componentId,
                  previousState: previousState
                }
              })
            );
          }, dispatchDelay);
        }

        // Dispatch trigger color event if enabled
        if (settings.enable_trigger_color_control && settings.trigger_button_selectors) {
          // Use the selectors directly as provided by the user
          const triggerSelector = settings.trigger_button_selectors.trim();

          if (triggerSelector) {
            window.dispatchEvent(
              new CustomEvent('trigger-color-change', {
                detail: {
                  state: state,
                  eventId: settings.event_id,
                  triggerSelector: triggerSelector,
                  colors: {
                    activeColor: settings.trigger_active_color || '#000000',
                    inactiveColor: settings.trigger_inactive_color || '#878787'
                  },
                  timestamp: Date.now()
                }
              })
            );
          }
        }

        if (state === 'visible') {
          element.style.display = 'block';
          element.style.pointerEvents = 'auto';

          // Create and show backdrop only if needed
          if (shouldHaveBackdrop) {
            this.createBackdrop();

            // Show backdrop immediately to prevent interference from closing elements
            if (this.backdropElement) {
              this.backdropElement.style.display = 'block';
              this.backdropElement.style.pointerEvents = 'auto';

              // Use immediate animation to ensure backdrop appears
              requestAnimationFrame(() => {
                if (this.backdropElement) {
                  this.backdropElement.style.opacity = '1';
                }
              });
            }
          }

          // Apply show animation based on type
          requestAnimationFrame(() => {
            switch (this.actionResponseType) {
              case 'drawer':
                element.style.transform = this.getDrawerVisibleTransform();
                break;
              case 'popup':
                element.style.transform = this.getPopupVisibleTransform();
                // Only set opacity for animations that use it
                const animationStyle = settings.popup_animation_style || 'fade';
                if (
                  animationStyle === 'fade' ||
                  animationStyle === 'zoom' ||
                  animationStyle === 'slide-up' ||
                  animationStyle === 'slide-down'
                ) {
                  element.style.opacity = '1';
                }
                break;
              case 'toggle':
                element.style.opacity = '1';
                break;
            }

            // Reset animation state after show animation completes
            const showDuration = settings.action_transition_duration || 300;
            setTimeout(() => {
              this.isAnimating = false;
            }, showDuration);
          });

          if (this.actionResponseType === 'drawer') {
            document.body.classList.add('no-scroll');
          }

        } else {
          // Apply hide animation
          switch (this.actionResponseType) {
            case 'drawer':
              element.style.transform = this.getDrawerHiddenTransform();
              break;
            case 'popup':
              element.style.transform = this.getPopupHiddenTransform();
              // Only set opacity for animations that use it
              const animationStyle = settings.popup_animation_style || 'fade';
              if (
                animationStyle === 'fade' ||
                animationStyle === 'zoom' ||
                animationStyle === 'slide-up' ||
                animationStyle === 'slide-down'
              ) {
                element.style.opacity = '0';
              }
              break;
            case 'toggle':
              element.style.opacity = '0';
              break;
          }

          // Hide backdrop immediately to prevent interference
          if (this.backdropElement && shouldHaveBackdrop) {
            // Store reference before removal
            const backdropToRemove = this.backdropElement;
            this.backdropElement = null; // Clear reference immediately

            backdropToRemove.style.opacity = '0';
            backdropToRemove.style.pointerEvents = 'none';

            // Remove after animation completes
            const duration = settings.action_transition_duration || 300;
            setTimeout(() => {
              if (backdropToRemove && backdropToRemove.parentNode) {
                backdropToRemove.remove();
              }
            }, duration);
          }

          // Hide element after transition
          const duration = settings.action_transition_duration || 300;
          setTimeout(() => {
            element.style.display = 'none';
            element.style.pointerEvents = 'none';

            // Reset animation state after hide animation completes
            this.isAnimating = false;
          }, duration);

          if (this.actionResponseType === 'drawer') {
            document.body.classList.remove('no-scroll');
          }
        }
      },

      // Get drawer transform values
      getDrawerVisibleTransform() {
        return 'translateX(0) translateY(0)';
      },

      getDrawerHiddenTransform() {
        const direction = settings.drawer_direction || 'right';
        switch (direction) {
          case 'left':
            return 'translateX(-100%)';
          case 'right':
            return 'translateX(100%)';
          case 'top':
            return 'translateY(-100%)';
          case 'bottom':
            return 'translateY(100%)';
          default:
            return 'translateX(100%)';
        }
      },

      // Get popup transform values
      getPopupVisibleTransform() {
        const position = settings.popup_position || 'center';
        const animationStyle = settings.popup_animation_style || 'fade';

        // Base positioning without animation effects
        const baseTransforms = {
          center: 'translate(-50%, -50%)',
          top: 'translateX(-50%)',
          bottom: 'translateX(-50%)',
          left: 'translateY(-50%)',
          right: 'translateY(-50%)',
          'top-left': 'translate(0, 0)',
          'top-right': 'translate(0, 0)',
          'bottom-left': 'translate(0, 0)',
          'bottom-right': 'translate(0, 0)'
        };

        const baseTransform = baseTransforms[position] || baseTransforms['center'];

        // Add animation-specific transforms for visible state
        switch (animationStyle) {
          case 'zoom':
            return `${baseTransform} scale(1)`;
          case 'slide-up':
            return `${baseTransform} translateY(0)`;
          case 'slide-down':
            return `${baseTransform} translateY(0)`;
          case 'fade':
          case 'none':
          default:
            return baseTransform;
        }
      },

      getPopupHiddenTransform() {
        const position = settings.popup_position || 'center';
        const animationStyle = settings.popup_animation_style || 'fade';

        // Base positioning without animation effects
        const baseTransforms = {
          center: 'translate(-50%, -50%)',
          top: 'translateX(-50%)',
          bottom: 'translateX(-50%)',
          left: 'translateY(-50%)',
          right: 'translateY(-50%)',
          'top-left': 'translate(0, 0)',
          'top-right': 'translate(0, 0)',
          'bottom-left': 'translate(0, 0)',
          'bottom-right': 'translate(0, 0)'
        };

        const baseTransform = baseTransforms[position] || baseTransforms['center'];

        // Add animation-specific transforms for hidden state
        switch (animationStyle) {
          case 'zoom':
            return `${baseTransform} scale(0.8)`;
          case 'slide-up':
            return `${baseTransform} translateY(30px)`;
          case 'slide-down':
            return `${baseTransform} translateY(-30px)`;
          case 'fade':
          case 'none':
          default:
            return baseTransform;
        }
      },

      // Method to handle reparent height calculation
      setupReparentHeightCalculation() {
        const reparentElement = document.querySelector(settings.reparent_to_selector);

        if (reparentElement) {
          const updateHeight = () => {
            const height = reparentElement.offsetHeight;
            this.$el.style.maxHeight = `calc(100dvh - ${height}px)`;
          };

          // Set initial height
          updateHeight();

          // Update on resize
          const resizeObserver = new ResizeObserver(() => {
            updateHeight();
          });
          resizeObserver.observe(reparentElement);

          // Store observer for cleanup
          this.reparentResizeObserver = resizeObserver;
        }
      },

      // Dispatch initial trigger color state
      dispatchInitialTriggerColorState(retryCount = 0) {
        if (!settings.enable_trigger_color_control || !settings.trigger_button_selectors) return;

        // Determine the initial state
        let initialState = 'visible'; // Default for non-action-response elements

        if (this.actionResponseEnabled && this.actionInitialState) {
          initialState = this.actionInitialState;
        }

        // Use the selectors directly as provided by the user
        const triggerSelector = settings.trigger_button_selectors.trim();

        if (triggerSelector) {
          // Check if any matching wrapper elements exist
          const matchingWrappers = document.querySelectorAll(triggerSelector);

          // Check if these wrappers contain actual React-rendered button/a elements
          let hasReactButtons = false;
          matchingWrappers.forEach((wrapper) => {
            if (wrapper.querySelector('button, a')) {
              hasReactButtons = true;
            }
          });

          if (matchingWrappers.length > 0 && hasReactButtons) {
            window.dispatchEvent(
              new CustomEvent('trigger-color-change', {
                detail: {
                  state: initialState,
                  eventId: settings.event_id,
                  triggerSelector: triggerSelector,
                  colors: {
                    activeColor: settings.trigger_active_color || '#000000',
                    inactiveColor: settings.trigger_inactive_color || '#878787'
                  },
                  timestamp: Date.now(),
                  isInitial: true
                }
              })
            );
          } else if (retryCount < 3) {
            // Retry up to 3 times with increasing delays
            setTimeout(() => {
              this.dispatchInitialTriggerColorState(retryCount + 1);
            }, 500 * (retryCount + 1)); // 500ms, 1000ms, 1500ms
          }
        }
      },

      // Alpine.js destroy lifecycle hook
      destroy() {
        // Cleanup click outside handler
        this.cleanupClickOutsideHandler();
        // Cleanup resize observer if it exists
        if (this.reparentResizeObserver) {
          this.reparentResizeObserver.disconnect();
        }
        // Cleanup backdrop if it exists
        if (this.backdropElement) {
          this.backdropElement.remove();
          this.backdropElement = null;
        }
      }
    };
  }
};
